import { useRef } from 'react'
import { LayoutChangeEvent, StyleSheet, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import Animated from 'react-native-reanimated'
import { BetKind, BoardState, LastAction, Player, PlayerStates } from '@ppwr/poker'
import ChipBlueGreen from '../assets/ChipsBlueGreen.svg'
import ChipOrangePurple from '../assets/ChipsOrangePurple.svg'
import ChipPinkMaroon from '../assets/ChipsPinkMaroon.svg'
import { useStore } from '../../../models'
import { useFlyAnimation } from '../../../screens/new-design/hooks/useFlyAnimation'
import { useResponsive } from '@/screens/new-design/hooks'

type Point = {
  x: number
  y: number
}

const calculateTranslation = (p1: Point, p2: Point) => {
  const translateX = p2.x + 12 - p1.x
  const translateY = p2.y - p1.y

  return {
    translateX,
    translateY
  }
}

export const BETTING_CHIP_SIZE = 20
const CHIPS_ARRAY = [ChipBlueGreen, ChipOrangePurple, ChipPinkMaroon]

const getAmountOfChips = ({
  boardState,
  lastAction,
  otherPlayersLastActions
}: {
  boardState: BoardState['tag']
  lastAction: LastAction['action']
  otherPlayersLastActions: LastAction['action'][]
}) => {
  const lastActionTyped = lastAction as BetKind
  if (boardState === 'deal') {
    if ([BetKind.SMALLBLIND].includes(lastActionTyped)) return 1
    if ([BetKind.BIGBLIND].includes(lastActionTyped)) return 2
    if ([BetKind.BET, BetKind.CALL, BetKind.RAISE, PlayerStates.AllIn.tag].includes(lastActionTyped)) return 3
    return 0
  }
  if (['flop', 'turn', 'river'].includes(boardState)) {
    if (([PlayerStates.AllIn.tag] as string[]).includes(lastAction as string)) return 3
    if ([BetKind.BET].includes(lastActionTyped)) return 1
    // action is Raise or Call, and there was a previous Raise, Call or Bet before
    if (
      [BetKind.RAISE, BetKind.CALL].includes(lastActionTyped) &&
      otherPlayersLastActions.some((a) => [BetKind.CALL, BetKind.BET, BetKind.RAISE].includes(a as BetKind))
    )
      return 3
    if ([BetKind.CALL, BetKind.RAISE].includes(lastActionTyped)) return 2
    return 0
  }
  return 0
}

export const AnimatedChips = ({
  player,
  chipsAmount,
  horizontal,
  animationStarted,
  potChipsPosition,
  bottom
}: {
  player: Player
  chipsAmount: number
  potChipsPosition: Point
  animationStarted: boolean
  horizontal?: boolean
  bottom?: boolean
}) => {
  const viewRef = useRef(null)
  const playerChipsPositionRef = useRef<Point>({ x: 0, y: 0 })
  const { verticalScale } = useResponsive()

  const { translateX, translateY } = calculateTranslation(playerChipsPositionRef.current, potChipsPosition)

  const { animatedStyle } = useFlyAnimation({
    animationStarted,
    translateX,
    translateY
  })

  const handleLayout = (event: LayoutChangeEvent) => {
    if (viewRef.current) {
      //@ts-ignore
      viewRef.current.measure((x, y, width, height, pageX, pageY) => {
        playerChipsPositionRef.current = { x: pageX, y: pageY }
      })
    }
  }

  return (
    <Animated.View
      ref={viewRef}
      onLayout={handleLayout}
      style={[animatedStyle, styles.container, horizontal ? styles.horizontal : {}, bottom && styles.bottom]}
    >
      {CHIPS_ARRAY.slice(0, chipsAmount).map((Chip, i) => (
        <Chip
          key={`betting-chips-${player.id}-${i}`}
          height={verticalScale(BETTING_CHIP_SIZE)}
          width={verticalScale(BETTING_CHIP_SIZE)}
        />
      ))}
    </Animated.View>
  )
}

export const PlayerBettingChips = observer(
  ({ player, bottom, horizontal }: { player: Player; horizontal?: boolean; bottom?: boolean }) => {
    const {
      game: { boardState, seatedPlayersLastActions, isGameOver, potChipsPosition, chipsAnimationStarted, isMyFirstGame }
    } = useStore()
    const lastAction = player?.lastAction?.action

    if (!(boardState && lastAction) || isGameOver)
      return <View style={{ height: bottom ? 0 : 20, backgroundColor: 'transparent' }} />

    const chipsAmount = getAmountOfChips({
      boardState: isMyFirstGame ? 'deal' : boardState,
      lastAction,
      otherPlayersLastActions: seatedPlayersLastActions
    })

    if (!chipsAmount) return <View style={{ height: bottom ? 0 : 20, backgroundColor: 'transparent' }} />

    return (
      <AnimatedChips
        player={player}
        chipsAmount={chipsAmount}
        horizontal={horizontal}
        animationStarted={chipsAnimationStarted}
        potChipsPosition={potChipsPosition}
        bottom={bottom}
      />
    )
  }
)

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    gap: -15
  },
  horizontal: {
    flexDirection: 'row'
  },
  bottom: {
    top: -48,
    position: 'absolute'
  }
})
